import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyEnikkBossMasterCatalog,
  applyBossMasterExport,
  loadBossMasterSyncStatus,
  previewEnikkBossMasterCatalog,
  previewBossMasterExport,
} from '../api/bossMasterSyncApi.js';
import {
  BOSS_MASTER_DEBUGGER_VERSION,
  openBossMasterDebuggerInstaller,
} from '../api/bossMasterDebuggerInstaller.js';
import { BossMasterCatalogPanel } from './BossMasterCatalogPanel.jsx';
import { BossSeasonAssignmentPanel } from './BossSeasonAssignmentPanel.jsx';
import '../../../styles/boss-master-debugger.css';

function formatDateTime(value) {
  if (!value) return '없음';
  try {
    return new Intl.DateTimeFormat('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '확인 불가';
  }
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function summarizePayload(payload) {
  const source = plainObject(payload);
  const seasons = Object.values(plainObject(source.seasons));
  const rootBosses = Array.isArray(source.bosses) ? source.bosses : [];
  const seasonBosses = seasons.flatMap((season) => Array.isArray(season?.bosses) ? season.bosses : []);
  const seasonNos = [...new Set(seasons
    .map((season) => Number(season?.raid_no || season?.season_no || season?.seasonNo || 0))
    .filter((value) => Number.isInteger(value) && value > 0))]
    .sort((a, b) => a - b);
  return {
    sourceName: String(source.source || source.debugger?.name || 'Tampermonkey 디버거 JSON').trim(),
    version: String(source.version || source.debugger?.version || '').trim(),
    seasonNos,
    seasonCount: seasons.length || (rootBosses.length ? 1 : 0),
    bossCount: seasonBosses.length + rootBosses.length,
    mappingCount: Array.isArray(source.confirmed_or_strongly_inferred_mappings)
      ? source.confirmed_or_strongly_inferred_mappings.length
      : Array.isArray(source.mappings) ? source.mappings.length : 0,
    candidateCount: Array.isArray(source.candidate_mappings) ? source.candidate_mappings.length : 0,
  };
}

function ResultNames({ title, items = [] }) {
  if (!items.length) return null;
  return (
    <div className="boss-master-sync-result-names">
      <strong>{title}</strong>
      <ul>
        {items.map((item) => {
          const image = item.imageStatus === 'verified' ? '이미지 확정'
            : item.imageStatus === 'inferred' ? '이미지 추정'
              : '이미지 없음';
          return <li key={`${item.iconId}:${item.bossName}`}><b>{item.bossName || '이름 확인 불가'}</b><span>{item.iconId || 'ID 없음'} · {image}</span></li>;
        })}
      </ul>
    </div>
  );
}

function BossMasterSyncContent({ mobile = false }) {
  const fileInputRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [jsonInput, setJsonInput] = useState('');
  const [fileName, setFileName] = useState('');
  const [parsedPayload, setParsedPayload] = useState(null);
  const [preview, setPreview] = useState(null);
  const [enikkPreview, setEnikkPreview] = useState(null);
  const [working, setWorking] = useState('');
  const [dragging, setDragging] = useState(false);
  const [notice, setNotice] = useState({ type: '', message: '' });
  const [enikkNotice, setEnikkNotice] = useState({ type: '', message: '' });

  const payloadSummary = useMemo(() => parsedPayload ? summarizePayload(parsedPayload) : null, [parsedPayload]);

  const refresh = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setWorking('refresh');
    try {
      setStatus(await loadBossMasterSyncStatus());
    } catch (error) {
      if (!quiet) setNotice({ type: 'error', message: error?.message || '보스 DB 상태를 불러오지 못했습니다.' });
    } finally {
      if (!quiet) setWorking('');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const inspectEnikk = async () => {
    if (working) return;
    setWorking('enikk-preview');
    setEnikkNotice({ type: '', message: '' });
    try {
      const result = await previewEnikkBossMasterCatalog();
      setEnikkPreview(result);
      setStatus(result);
      setEnikkNotice({
        type: 'success',
        message: `시즌 ${result.sourceDetails?.seasonCount || 0}개에서 보스 ${result.capturedCount || 0}개와 코어·부위 정보를 확인했습니다.`,
      });
    } catch (error) {
      setEnikkPreview(null);
      setEnikkNotice({ type: 'error', message: error?.message || 'Enikk 공개 보스 목록을 확인하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const applyEnikk = async () => {
    if (!enikkPreview?.complete || working) return;
    if (!window.confirm(
      `Enikk 공개 상세의 중복 없는 보스 ${enikkPreview.capturedCount || 0}개를 Boss Master에 반영할까요?\n코어·부위 사실만 채우며 약점 속성은 저장하지 않습니다.`,
    )) return;

    setWorking('enikk-apply');
    setEnikkNotice({ type: '', message: '' });
    try {
      const result = await applyEnikkBossMasterCatalog();
      setEnikkPreview(result);
      setStatus(result);
      setEnikkNotice({
        type: 'success',
        message: `반영 완료 · 신규 ${result.insertedCount || 0}개 · 변경 ${result.updatedCount || 0}개 · 유지 ${result.unchangedCount || 0}개`,
      });
      window.dispatchEvent(new CustomEvent('dororaid:boss-catalog-changed'));
    } catch (error) {
      setEnikkNotice({ type: 'error', message: error?.message || 'Enikk 보스 정보를 반영하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const clearPreview = () => {
    setParsedPayload(null);
    setPreview(null);
  };

  const setJsonText = (text, nextFileName = '') => {
    setJsonInput(text);
    setFileName(nextFileName);
    clearPreview();
    setNotice({ type: '', message: '' });
  };

  const readFile = async (file) => {
    if (!file) return;
    if (!/\.json$/i.test(file.name) && file.type !== 'application/json') {
      setNotice({ type: 'error', message: 'JSON 파일만 가져올 수 있습니다.' });
      return;
    }
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      setJsonInput(text);
      setFileName(file.name);
      setParsedPayload(payload);
      setPreview(null);
      const summary = summarizePayload(payload);
      setNotice({ type: 'success', message: `${file.name} · ${summary.seasonCount}개 차수 · 보스 항목 ${summary.bossCount}개를 불러왔습니다.` });
    } catch {
      setNotice({ type: 'error', message: '읽을 수 있는 JSON 파일이 아닙니다.' });
    }
  };

  const parseJson = () => {
    if (!jsonInput.trim()) throw new Error('Tampermonkey 디버거 JSON을 선택하거나 붙여넣어주세요.');
    try {
      return JSON.parse(jsonInput);
    } catch {
      throw new Error('JSON 형식이 올바르지 않습니다.');
    }
  };

  const inspect = async () => {
    if (working) return;
    let payload;
    try {
      payload = parseJson();
      setParsedPayload(payload);
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || 'JSON을 확인해주세요.' });
      return;
    }

    setWorking('preview');
    setPreview(null);
    setNotice({ type: '', message: '' });
    try {
      const result = await previewBossMasterExport(payload);
      setPreview(result);
      setNotice({
        type: 'success',
        message: `확인 완료 · 신규 ${result.insertedCount}개 · 변경 ${result.updatedCount}개 · 유지 ${result.unchangedCount}개`,
      });
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '보스 JSON을 확인하지 못했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const apply = async () => {
    if (!preview?.complete || !parsedPayload || working) return;
    if (!window.confirm(
      `확인된 보스 ${preview.capturedCount || 0}개를 공용 DB에 반영할까요?\n기존 보스는 삭제하지 않고 신규·변경 정보만 저장합니다.`,
    )) return;

    setWorking('apply');
    setNotice({ type: '', message: '' });
    try {
      const result = await applyBossMasterExport(parsedPayload);
      setPreview(result);
      setNotice({
        type: 'success',
        message: `DB 반영 완료 · 신규 ${result.insertedCount}개 · 변경 ${result.updatedCount}개 · 유지 ${result.unchangedCount}개`,
      });
      await refresh({ quiet: true });
      window.dispatchEvent(new CustomEvent('dororaid:boss-catalog-changed'));
    } catch (error) {
      setNotice({ type: 'error', message: error?.message || '공용 보스 DB 반영에 실패했습니다.' });
    } finally {
      setWorking('');
    }
  };

  const clear = () => {
    setJsonInput('');
    setFileName('');
    clearPreview();
    setNotice({ type: '', message: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const latestBatch = status?.latestBatch || null;

  return (
    <section className={`boss-master-sync-board ${mobile ? 'mobile' : 'desktop'}`} aria-label="보스 DB 저장">
      {/* 이 패널은 '보스 DB 관리' 탭 안에 들어 있다. '플랫폼 관리 / 보스 DB …' 머리글을 한 화면에
          두 번 그리고 있었으므로 여기서는 탭 제목만 남긴다. */}
      <header>
        <div>
          <h2>데이터 수집 · 동기화</h2>
          <p>DoroParty 보스 DB 디버거로 BlablaLink의 보스 정보와 이미지를 연결해 수집한 뒤 JSON을 확인하고 반영합니다.</p>
        </div>
        <button type="button" className="secondary" disabled={Boolean(working)} onClick={() => refresh()}>
          {working === 'refresh' ? '새로고침 중…' : '새로고침'}
        </button>
      </header>

      <div className="boss-master-sync-kpis">
        <div><span>저장된 보스</span><strong>{Number(status?.visibleCount || 0).toLocaleString('ko-KR')}개</strong></div>
        <div><span>이미지 있음</span><strong>{Number(status?.withImageCount || 0).toLocaleString('ko-KR')}개</strong></div>
        <div><span>이미지 미확정</span><strong>{Number(status?.unresolvedImageCount || 0).toLocaleString('ko-KR')}개</strong></div>
        <div><span>최근 반영</span><strong>{latestBatch?.applied ? `${latestBatch.inserted_count || 0}+${latestBatch.updated_count || 0}` : '없음'}</strong><small>{formatDateTime(latestBatch?.created_at)}</small></div>
      </div>

      <section className="boss-master-enikk-import" aria-label="Enikk 공개 보스 상세 가져오기">
        <div className="boss-master-enikk-import-head">
          <div>
            <strong>Enikk 공개 유니온 레이드 상세</strong>
            <p>35시즌 이후 같은 보스의 속성 변형은 하나로 합치고 코어·공격 가능 부위 사실을 가져옵니다. 약점은 시즌 설정에서 선택합니다.</p>
          </div>
          <a href="https://enikk.app/unionraid/" target="_blank" rel="noreferrer">원본 보기</a>
        </div>
        <div className="boss-master-enikk-import-actions">
          <button type="button" className="secondary" disabled={Boolean(working)} onClick={inspectEnikk}>
            {working === 'enikk-preview' ? '공개 상세 확인 중…' : '공개 상세 확인'}
          </button>
          <button type="button" className="apply" disabled={!enikkPreview?.complete || Boolean(working)} onClick={applyEnikk}>
            {working === 'enikk-apply' ? 'Boss Master 반영 중…' : '확인 결과 반영'}
          </button>
        </div>
        {enikkPreview ? (
          <div className="boss-master-enikk-import-summary">
            <div><span>수집 시즌</span><strong>{enikkPreview.sourceDetails?.seasonCount || 0}개</strong></div>
            <div><span>시즌 배치</span><strong>{enikkPreview.sourceDetails?.placementCount || 0}개</strong></div>
            <div><span>고유 보스</span><strong>{enikkPreview.capturedCount || 0}개</strong></div>
            <div><span>기존 DB 연결</span><strong>{enikkPreview.sourceDetails?.matchedCatalogCount || 0}개</strong></div>
            <div><span>코어·부위 확인</span><strong>{enikkPreview.sourceDetails?.mechanicsKnownCount || 0}개</strong></div>
            <div><span>중복 통합</span><strong>{enikkPreview.sourceDetails?.collapsedDuplicateCount || 0}개</strong></div>
          </div>
        ) : null}
        {enikkPreview?.warnings?.length ? <p className="boss-master-enikk-import-note">{enikkPreview.warnings.join(' ')}</p> : null}
        {enikkNotice.message ? <p className={`boss-master-sync-notice ${enikkNotice.type}`} role="status">{enikkNotice.message}</p> : null}
      </section>

      <div className="boss-master-debugger-setup">
        <div>
          <strong>보스 DB 디버거 v{BOSS_MASTER_DEBUGGER_VERSION}</strong>
          <p>기존 네트워크 스니퍼처럼 URL만 따로 모으지 않고, API 응답·보스 카드 DOM·이미지 로드 근거를 함께 기록합니다.</p>
          <ol>
            <li>디버거를 Tampermonkey에 설치합니다.</li>
            <li>BlablaLink 유니온 레이드에서 차수를 열고 디버거의 현재 차수를 맞춥니다.</li>
            <li>현재 화면 재스캔 후 다른 차수도 반복하고 JSON을 다운로드합니다.</li>
          </ol>
        </div>
        <button type="button" onClick={openBossMasterDebuggerInstaller}>보스 DB 디버거 설치</button>
      </div>

      <div className="boss-master-sync-step-head">
        <b>1</b>
        <div><strong>디버거로 차수별 수집</strong><p>같은 응답 객체나 보스 카드 DOM에서 직접 연결된 이미지만 확정 매핑으로 내보냅니다.</p></div>
      </div>

      <div className="boss-master-sync-step-head">
        <b>2</b>
        <div><strong>디버거 JSON 가져오기</strong><p>파일을 놓거나 선택하세요. JSON 내용을 직접 붙여넣어도 됩니다.</p></div>
      </div>

      <div
        className={`boss-master-sync-dropzone ${dragging ? 'dragging' : ''}`}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { event.preventDefault(); if (event.currentTarget === event.target) setDragging(false); }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          readFile(event.dataTransfer.files?.[0]);
        }}
      >
        <div><strong>{fileName || 'JSON 파일을 여기에 놓으세요'}</strong><span>{fileName ? '다른 파일을 선택하면 현재 입력을 교체합니다.' : '또는 아래 버튼으로 파일을 선택합니다.'}</span></div>
        <button type="button" disabled={Boolean(working)} onClick={() => fileInputRef.current?.click()}>JSON 파일 선택</button>
        <input ref={fileInputRef} type="file" accept="application/json,.json" disabled={Boolean(working)} onChange={(event) => readFile(event.target.files?.[0])} />
      </div>

      <label className="boss-master-sync-json-input">
        <span>JSON 내용</span>
        <textarea
          value={jsonInput}
          placeholder="DoroParty 보스 DB 디버거 JSON 붙여넣기"
          disabled={Boolean(working)}
          onChange={(event) => setJsonText(event.target.value)}
        />
      </label>

      {payloadSummary ? (
        <div className="boss-master-sync-file-summary">
          <div><span>수집기</span><strong>{payloadSummary.sourceName}</strong><small>{payloadSummary.version ? `v${payloadSummary.version}` : '버전 정보 없음'}</small></div>
          <div><span>수집 차수</span><strong>{payloadSummary.seasonCount}개</strong><small>{payloadSummary.seasonNos.length ? `${payloadSummary.seasonNos.join(', ')}차` : '차수 번호 확인 필요'}</small></div>
          <div><span>보스 항목</span><strong>{payloadSummary.bossCount}개</strong><small>중복 시즌은 확인 단계에서 병합</small></div>
          <div><span>확정 이미지</span><strong>{payloadSummary.mappingCount}개</strong><small>{payloadSummary.candidateCount ? `후보 ${payloadSummary.candidateCount}개는 자동 저장 제외` : '직접 연결된 매핑만 반영'}</small></div>
        </div>
      ) : null}

      <div className="boss-master-sync-primary-action">
        <button type="button" disabled={Boolean(working) || !jsonInput.trim()} onClick={inspect}>
          {working === 'preview' ? '수집 내용 확인 중…' : '수집 내용 확인'}
        </button>
      </div>

      <div className="boss-master-sync-step-head">
        <b>3</b>
        <div><strong>변경 내용 확인</strong><p>신규·변경·유지 항목과 이미지 미확정 항목을 확인합니다.</p></div>
      </div>

      <div className={`boss-master-sync-validation ${preview ? preview.complete ? 'complete' : 'partial' : 'empty'}`}>
        <div><span>확인 상태</span><strong>{preview ? preview.complete ? '반영 가능' : '확인 필요' : '대기'}</strong><small>{preview ? `${preview.capturedCount}개 공용 보스로 정리됨` : 'JSON을 가져온 뒤 수집 내용 확인을 누르세요.'}</small></div>
        <div><span>이미지 처리</span><strong>{preview ? `${preview.unresolvedImageCount}개 미확정` : '-'}</strong><small>{preview?.warnings?.[0] || '이미지가 없어도 보스 기본 정보는 저장됩니다.'}</small></div>
      </div>

      {preview ? (
        <div className="boss-master-sync-result">
          <div><span>신규</span><strong>{preview.insertedCount}</strong></div>
          <div><span>변경</span><strong>{preview.updatedCount}</strong></div>
          <div><span>유지</span><strong>{preview.unchangedCount}</strong></div>
          <div><span>이미지 미확정</span><strong>{preview.unresolvedImageCount}</strong></div>
          <ResultNames title="신규 저장 예정" items={preview.insertedBosses} />
          <ResultNames title="변경 예정" items={preview.updatedBosses} />
          {preview.warnings?.length ? <p className="boss-master-sync-warnings">{preview.warnings.join(' ')}</p> : null}
        </div>
      ) : null}

      <div className="boss-master-sync-step-head">
        <b>4</b>
        <div><strong>공용 DB 반영</strong><p>확인 결과가 맞을 때만 반영합니다. 기존 보스는 자동 삭제하지 않습니다.</p></div>
      </div>

      <div className="boss-master-sync-footer-actions">
        <button type="button" className="apply" disabled={!preview?.complete || Boolean(working)} onClick={apply}>
          {working === 'apply' ? 'DB 반영 중…' : '공용 DB 반영'}
        </button>
        <button type="button" className="secondary" disabled={Boolean(working) || (!jsonInput && !preview)} onClick={clear}>초기화</button>
      </div>

      {notice.message ? <p className={`boss-master-sync-notice ${notice.type}`} role="status">{notice.message}</p> : null}
    </section>
  );
}

export function BossMasterSyncBoard({ mobile = false }) {
  const [activeView, setActiveView] = useState('list');
  const tabs = [
    ['list', '보스 목록'],
    ['detail', '보스 상세 편집'],
    ...(!mobile ? [['season', '시즌 보스 배치']] : []),
    ['sync', '데이터 수집/동기화'],
  ];

  return (
    <div className={`boss-master-management ${mobile ? 'mobile' : 'desktop'}`}>
      <header className="boss-master-management-header">
        <div>
          <p className="section-eyebrow">플랫폼 관리</p>
          <h2>보스 DB 관리</h2>
          <p>재사용 가능한 보스 사실 정보와 BlablaLink 수집·동기화를 관리합니다.</p>
        </div>
      </header>
      <nav className="boss-master-management-tabs" aria-label="보스 DB 메뉴">
        {tabs.map(([value, label]) => (
          <button type="button" key={value} className={activeView === value ? 'active' : ''} onClick={() => setActiveView(value)}>
            {label}
          </button>
        ))}
      </nav>
      {activeView === 'sync' ? <BossMasterSyncContent mobile={mobile} /> : null}
      {activeView === 'season' ? <BossSeasonAssignmentPanel /> : null}
      {activeView === 'list' || activeView === 'detail'
        ? <BossMasterCatalogPanel view={activeView} onOpenEditor={() => setActiveView('detail')} />
        : null}
    </div>
  );
}
